import {oktaAccessToken} from '@/config/oktaConfig'
import {ENDPOINTS} from '@/utils/constants/apiEndpoints'
import {clearOktaTokens} from '@/utils/authUtils'
import {API_BASE_URL} from '@/config/envConfig'

/**
 * Service to handle authentication token exchange and user data
 * This mirrors the Angular AuthService functionality
 */
export class AuthService {
  /**
   * Exchanges Okta token for backend token (same as Angular's getOktaToken method)
   * @returns Promise<string> - The backend token containing user roles
   */
  static async exchangeTokenForBackendToken(): Promise<string | null> {
    try {
      const oktaToken = oktaAccessToken()
      if (!oktaToken) {
        return null
      }

      // Use the same pattern as other services - get base URL from environment
      const baseURL = API_BASE_URL
      if (!baseURL) {
        return null
      }

      // Call the same endpoint that Angular uses, using the constant
      const loginUrl = `${baseURL}${ENDPOINTS.LOGIN}`

      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${oktaToken}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({}), // Empty body like Angular
      })

      if (!response.ok) {
        throw new Error(`Token exchange failed: ${response.status} ${response.statusText}`)
      }

      // Extract token from response header (same as Angular)
      const backendToken = response.headers.get('x-authentication-token')

      if (backendToken) {
        // Store in localStorage (same as Angular)
        localStorage.setItem('access_token', backendToken)
        return backendToken
      } else {
        return null
      }
    } catch (error) {
      return null
    }
  }

  /**
   * Gets the backend token from localStorage (same as Angular's getToken method)
   * @returns string | null - The backend token
   */
  static getToken(): string | null {
    return localStorage.getItem('access_token')
  }

  /**
   * Clears all authentication tokens (same as Angular's logout method)
   * Enhanced with comprehensive cleanup like B2B Portal
   */
  static logout(): void {
    // Clear backend tokens
    localStorage.removeItem('access_token')
    localStorage.removeItem('auth-code')

    // Clear Okta tokens
    clearOktaTokens()

    // Clear any additional auth-related data
    localStorage.removeItem('rememberMe')
    localStorage.removeItem('secureId')
    localStorage.removeItem('sessionTimeOut')
    localStorage.removeItem('extendTokenExpiration')
    localStorage.removeItem('accessTokenExpiresAt')

    // Clear global flags
    ;(globalThis as any).session401Flag = false
    ;(globalThis as any).logoutInProgress = false
  }

  /**
   * Checks if user has a valid backend token
   * @returns boolean - True if backend token exists
   */
  static hasBackendToken(): boolean {
    return !!this.getToken()
  }
}
